import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Checkbox, FormControlLabel, Popover, TextField, Typography, useTheme } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import {
    getPerformancePickerTextFieldProps, performanceCompactLabelSx, performancePickerPopperSx,
    performancePopoverPaperSx, performanceSecondaryControlSx,
} from '../../styles/performanceMsiStyles';
import { EVENT_NOTES_MAX, EVENT_TITLE_MAX, MAX_TABLE_COLUMNS } from './exploreColumns';
import { formatDay, parsePickerDate, pickerToIso } from './exploreFormat';

const sameTitle = (left, right) => left.trim().toLowerCase() === right.trim().toLowerCase();

// Why the draft can't be saved yet, or null.
function draftProblem(draft, takenTitles, firstDate, lastDate) {
    if (!draft.title.trim()) return 'Give the event a title.';
    if (takenTitles.some((title) => sameTitle(title, draft.title))) return `You already have an event called “${draft.title.trim()}”.`;
    if (!draft.start || !draft.end) return 'Choose a start date and an end date.';
    if (draft.start > draft.end) return 'The end date must be on or after the start date.';
    if ((firstDate && draft.start < firstDate) || (lastDate && draft.end > lastDate)) {
        return `Choose dates within the factor history, ${formatDay(firstDate || draft.start)} to ${formatDay(lastDate || draft.end)}.`;
    }
    return null;
}

/**
 * The "New event" or "Edit event" pop-out: a title (required, unique among
 * your events ignoring case), start and end dates inside the factor history
 * when it is known, optional notes and, for a new event, whether it goes
 * straight into the table (not while the table is full). `onSave(draft,
 * { addToTable })` resolves once the event is stored, or rejects with the
 * reason, which shows here. The draft starts from `initial` when the pop-out
 * mounts, so the caller keys a fresh one for every opening.
 */
export default function EventEditor({
    open, anchorEl, mode = 'create', initial, takenTitles = [], firstDate = null, lastDate = null, tableFull = false,
    onClose, onSave,
}) {
    const theme = useTheme();
    const [draft, setDraft] = useState({
        title: initial.title || '', start: initial.start_date || null, end: initial.end_date || null, notes: initial.notes || '',
    });
    const [addToTable, setAddToTable] = useState(!tableFull);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const titleTaken = takenTitles.some((title) => draft.title.trim() && sameTitle(title, draft.title));
    const change = (field) => (value) => {
        setError(null);
        setDraft((old) => ({ ...old, [field]: value }));
    };
    const submit = async (event) => {
        event.preventDefault();
        const problem = draftProblem(draft, takenTitles, firstDate, lastDate);
        if (problem) {
            setError(problem);
            return;
        }
        setSaving(true);
        try {
            await onSave(
                { title: draft.title.trim(), start_date: draft.start, end_date: draft.end, notes: draft.notes.trim() },
                { addToTable: mode === 'create' && addToTable && !tableFull },
            );
        } catch (failure) {
            setError(failure?.message || 'The event couldn’t be saved.');
            setSaving(false);
        }
    };
    const pickerSlots = { textField: getPerformancePickerTextFieldProps(theme), popper: { sx: performancePickerPopperSx } };
    const heading = mode === 'edit' ? 'Edit event' : 'New event';
    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={() => { if (!saving) onClose(); }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{ paper: { sx: { mt: 0.6, p: 1.5, width: 320, maxWidth: 'calc(100vw - 32px)', ...performancePopoverPaperSx } } }}
        >
            <Box component="form" noValidate aria-label={heading} onSubmit={submit} sx={{ display: 'grid', gap: 1 }}>
                <Typography variant="caption" sx={{ ...performanceCompactLabelSx, fontWeight: 700 }}>{heading}</Typography>
                <TextField
                    size="small"
                    label="Title"
                    required
                    autoFocus
                    value={draft.title}
                    onChange={(event) => change('title')(event.target.value)}
                    error={titleTaken}
                    helperText={titleTaken ? 'You already have an event with this title.' : null}
                    inputProps={{ maxLength: EVENT_TITLE_MAX }}
                />
                <DatePicker
                    label="Start"
                    value={parsePickerDate(draft.start)}
                    minDate={parsePickerDate(firstDate) || undefined}
                    maxDate={parsePickerDate(draft.end || lastDate) || undefined}
                    onChange={(value) => change('start')(pickerToIso(value))}
                    slotProps={pickerSlots}
                />
                <DatePicker
                    label="End"
                    value={parsePickerDate(draft.end)}
                    minDate={parsePickerDate(draft.start || firstDate) || undefined}
                    maxDate={parsePickerDate(lastDate) || undefined}
                    referenceDate={parsePickerDate(draft.start) || undefined}
                    onChange={(value) => change('end')(pickerToIso(value))}
                    slotProps={pickerSlots}
                />
                {firstDate && lastDate && (
                    <Typography variant="caption" color="text.secondary">
                        Factor history runs from {formatDay(firstDate)} to {formatDay(lastDate)}.
                    </Typography>
                )}
                <TextField
                    size="small"
                    label="Notes (optional)"
                    multiline
                    minRows={2}
                    maxRows={6}
                    value={draft.notes}
                    onChange={(event) => change('notes')(event.target.value)}
                    inputProps={{ maxLength: EVENT_NOTES_MAX }}
                />
                {mode === 'create' && (
                    <FormControlLabel
                        control={<Checkbox size="small" checked={addToTable && !tableFull} disabled={tableFull} onChange={(event) => setAddToTable(event.target.checked)} />}
                        label={tableFull ? `Add to the table (it holds ${MAX_TABLE_COLUMNS} and is full)` : 'Add to the table'}
                        slotProps={{ typography: { variant: 'body2' } }}
                    />
                )}
                {error && <Typography role="alert" variant="caption" color="error">{error}</Typography>}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button size="small" onClick={onClose} disabled={saving} sx={performanceSecondaryControlSx}>Cancel</Button>
                    <Button size="small" variant="contained" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save event'}</Button>
                </Box>
            </Box>
        </Popover>
    );
}

EventEditor.propTypes = {
    open: PropTypes.bool.isRequired,
    anchorEl: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    mode: PropTypes.oneOf(['create', 'edit']),
    initial: PropTypes.shape({
        title: PropTypes.string, start_date: PropTypes.string, end_date: PropTypes.string, notes: PropTypes.string,
    }).isRequired,
    takenTitles: PropTypes.arrayOf(PropTypes.string),
    firstDate: PropTypes.string,
    lastDate: PropTypes.string,
    tableFull: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
};
